import React, { useMemo } from "react";
import { Divider, Typography } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import useLanguage from "../../hooks/useLanguage";
import { Page, PageHeader, PageMetaContainer, ActionsWrapper } from "./style";
import IButton from "../iconButton";

/**
 * CommonPage component renders a page with optional title, subtitle, and action.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.title - The title of the page.
 * @param {string} props.subtitle - The subtitle of the page.
 * @param {React.ReactNode} props.children - The content to be displayed within the page.
 * @param {Object} props.action - The action object containing label and onClick handler.
 * @param {string} props.action.label - The label for the action.
 * @param {function} props.action.onClick - The onClick handler for the action.
 * @param {React.ReactNode} props.action.icon - React node to be displayed as the action icon.
 * @param {Boolean} props.action.disabled - Whether the action button is disabled.
 * @param {React.ReactNode[]} props.actions - The list of action elements to be displayed.
 */
export default function CommonPage({
  title,
  subtitle,
  action,
  actions,
  children,
  maxWidth = "lg",
}) {
  const [{ direction }] = useLanguage();
  const isAction = useMemo(
    () => !!action && !!action.onClick && !!action.label && !actions?.length,
    [action],
  );
  const isActions = useMemo(() => actions?.length > 0, [actions]);
  return (
    <Page maxWidth={maxWidth} direction={direction}>
      <PageMetaContainer>
        <PageHeader>
          {title && <Typography variant="h4">{title}</Typography>}
          {isAction && (
            <IButton
              startIcon={action?.icon || <AddIcon />}
              variant={action?.disabled ? "outlined" : "contained"}
              onClick={action.onClick}
              color="action"
              disabled={action?.disabled}
            >
              {action.label}
            </IButton>
          )}
          {isActions && (
            <ActionsWrapper>{actions.map((A) => A)}</ActionsWrapper>
          )}
        </PageHeader>
        {subtitle && <Typography variant="caption">{subtitle}</Typography>}
        {(title || subtitle) && <Divider />}
        {children}
      </PageMetaContainer>
    </Page>
  );
}
